import React, { useEffect, useState } from 'react'
import { avatarColor, scoreColor, STAGE_MAP } from './crmData'

/** Coloured rounded-square initial used on deal cards + table rows. Shows the company's real
 * logo when WeQuote sends one (deal.logoUrl); falls back to the initial on missing/broken URLs. */
export const InitialBox: React.FC<{ name: string; size?: number; logoUrl?: string }> = ({ name, size = 32, logoUrl }) => {
  const [broken, setBroken] = useState(false)
  if (logoUrl && !broken)
    return (
      <img
        src={logoUrl}
        alt={name}
        className="rounded-md shrink-0 object-contain bg-white border border-border"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
        loading="lazy"
      />
    )
  return (
    <span
      className="inline-flex items-center justify-center rounded-md text-white font-semibold shrink-0"
      style={{ width: size, height: size, background: avatarColor(name), fontSize: size * 0.4 }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  )
}

/** Round avatar with initial (Customer Success / Contacts / team). Logo-aware like InitialBox. */
export const Avatar: React.FC<{ name: string; size?: number; title?: string; logoUrl?: string }> = ({ name, size = 28, title, logoUrl }) => {
  const [broken, setBroken] = useState(false)
  if (logoUrl && !broken)
    return (
      <img
        src={logoUrl}
        alt={name}
        title={title}
        className="rounded-full shrink-0 object-contain bg-white border border-border"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
        loading="lazy"
      />
    )
  return (
    <span
      title={title}
      className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width: size, height: size, background: avatarColor(name), fontSize: size * 0.4 }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  )
}

/** Standard search bar — identical, PROMINENT look on every CRM page: magnifier icon, brand
 * tinted border with a soft glow, clear button when there's a query. Use `compact` for tight
 * spots (kanban column headers). */
export const SearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; className?: string; compact?: boolean }> = ({ value, onChange, placeholder = 'Search…', className = '', compact = false }) => (
  <div className={`relative ${className}`}>
    <svg
      className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-500 pointer-events-none ${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
    </svg>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full rounded-lg border bg-surface text-txt-primary placeholder:text-txt-tertiary focus:outline-none transition-shadow ${
        compact ? 'pl-8 pr-7 py-1.5 text-xs' : 'pl-9 pr-8 py-2 text-sm'
      } ${
        value
          ? 'border-brand-500 shadow-[0_0_0_3px_rgba(37,99,235,0.15)]'
          : 'border-brand-300 shadow-[0_0_0_3px_rgba(37,99,235,0.06)] focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)]'
      }`}
    />
    {value && (
      <button
        onClick={() => onChange('')}
        title="Clear search"
        className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-surface-tertiary text-txt-secondary hover:bg-brand-100 hover:text-brand-700 flex items-center justify-center font-bold ${compact ? 'w-4 h-4 text-[9px]' : 'w-5 h-5 text-[10px]'}`}
      >
        ✕
      </button>
    )}
  </div>
)

/* ── Shared monochrome icons (UI audit 21 Aug: colour emojis were ruining the
 *    minimal look — these inherit currentColor and sit on any pill/button). ── */
const Ic: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg className={className || 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={d} /></svg>
)
export const IconPhone: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
export const IconMailIn: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
export const IconMailOut: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="m22 2-7 20-4-9-9-4 20-7zM22 2 11 13" />
export const IconPaperclip: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
export const IconCalendarUi: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
export const IconClock: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2" />
export const IconAlert: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3zM12 9v4M12 17h.01" />
export const IconTrash: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
export const IconUpload: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
export const IconVideo: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="m22 8-6 4 6 4V8zM14 6H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z" />
export const IconChatUi: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
export const IconLink: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
export const IconRefresh: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M3 12a9 9 0 0 1 15-6.7L21 8M21 12a9 9 0 0 1-15 6.7L3 16M21 3v5h-5M3 21v-5h5" />
export const IconLock: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4" />
export const IconBell: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
export const IconNote: React.FC<{ className?: string }> = ({ className }) => <Ic className={className} d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />

/** Engagement score chip: a coloured dot + number (green/amber/red by value). */
export const ScoreDot: React.FC<{ score: number }> = ({ score }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-txt-secondary">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: scoreColor(score) }} />
    {score}
  </span>
)

/** Lead-score badge (sh/sm2/sl): coloured pill with dot + number. */
export const ScoreBadge: React.FC<{ score: number; suffix?: string; className?: string }> = ({ score, suffix, className = '' }) => {
  const tone =
    score >= 70
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : score >= 40
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-tight ${tone} ${className}`}>
      <span className="w-1 h-1 rounded-full" style={{ background: scoreColor(score) }} />
      {score}
      {suffix}
    </span>
  )
}

type PillTone = 'red' | 'amber' | 'blue' | 'green' | 'gray' | 'purple'
const TONES: Record<PillTone, string> = {
  red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  blue: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  gray: 'bg-surface-tertiary text-txt-secondary',
  purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
}

export const Pill: React.FC<{ tone: PillTone; children: React.ReactNode; className?: string }> = ({ tone, children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-tight ${TONES[tone]} ${className}`}>
    {children}
  </span>
)

/** Assigned CRM flags as small coloured chips (shared marker layer). */
export const FlagChips: React.FC<{ flags?: { id: string; name: string; color: string }[]; className?: string }> = ({ flags, className = '' }) => {
  if (!flags || flags.length === 0) return null
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {flags.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-tight" style={{ background: f.color + '22', color: f.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: f.color }} />
          {f.name}
        </span>
      ))}
    </span>
  )
}

/** Solid stage pill (white text on the stage colour). */
export const StageBadge: React.FC<{ stageKey: string; className?: string }> = ({ stageKey, className = '' }) => {
  const s = STAGE_MAP[stageKey]
  if (!s) return null
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold text-white whitespace-nowrap ${className}`} style={{ background: s.color }}>
      {s.label}
    </span>
  )
}

/** Coloured status badge from an arbitrary {colour,bg,label}. */
export const StatusBadge: React.FC<{ label: string; colour: string; bg: string }> = ({ label, colour, bg }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: colour, background: bg }}>
    {label}
  </span>
)

/** KPI tile used on Analytics / Customer Success / Opportunities. */
export const Kpi: React.FC<{ label: string; value: React.ReactNode; tone?: 'default' | 'blue' | 'green' | 'amber' | 'red'; warn?: boolean }> = ({
  label,
  value,
  tone = 'default',
  warn = false,
}) => {
  const valTone = {
    default: 'text-txt-primary',
    blue: 'text-brand-600',
    green: 'text-success',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  }[tone]
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'bg-amber-50/60 border-amber-200 dark:bg-amber-900/10 dark:border-amber-900/40' : 'bg-surface border-border'}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-txt-tertiary">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valTone}`}>{value}</p>
    </div>
  )
}

/** Horizontal funnel/bar row: label · filled bar (with inner text) · right percent. */
export const FunnelRow: React.FC<{
  label: string
  barPct: number
  color: string
  inner?: React.ReactNode
  right?: React.ReactNode
  rightColor?: string
}> = ({ label, barPct, color, inner, right, rightColor }) => (
  <div className="flex items-center gap-3">
    <span className="w-44 shrink-0 text-xs font-medium text-txt-secondary truncate">{label}</span>
    <div className="flex-1 h-6 rounded-md bg-surface-tertiary overflow-hidden">
      <div className="h-full rounded-md flex items-center px-2 text-[11px] font-semibold text-white" style={{ width: `${Math.max(barPct, 6)}%`, background: color }}>
        {inner}
      </div>
    </div>
    {right != null && (
      <span className="w-24 shrink-0 text-right text-xs font-semibold" style={{ color: rightColor || 'var(--color-txt-secondary)' }}>
        {right}
      </span>
    )}
  </div>
)

/** Thin progress bar. */
export const Bar: React.FC<{ pct: number; color: string; className?: string }> = ({ pct, color, className = '' }) => (
  <div className={`h-1.5 rounded-full bg-surface-tertiary overflow-hidden ${className}`}>
    <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} />
  </div>
)

/** Card with optional title/subtitle header. */
export const Card: React.FC<{ title?: string; subtitle?: string; actions?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  subtitle,
  actions,
  children,
  className = '',
}) => (
  <div className={`rounded-xl bg-surface border border-border ${className}`}>
    {(title || actions) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div>
          {title && <p className="text-sm font-semibold text-txt-primary">{title}</p>}
          {subtitle && <p className="text-xs text-txt-tertiary mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className={title || actions ? 'px-5 pb-5' : 'p-5'}>{children}</div>
  </div>
)

/**
 * Segmented page-level view toggle (the blue "Map / List" control). Use for switching whole
 * views — Card/Table, Analytics sections, Settings sections. In-page FILTERS keep the pill
 * style; this is for "which view am I looking at".
 */
export interface SegTab { key: string; label: string; icon?: React.ReactNode }
export const SegTabs: React.FC<{ tabs: SegTab[]; value: string; onChange: (key: string) => void; size?: 'sm' | 'md' }> = ({
  tabs,
  value,
  onChange,
  size = 'md',
}) => {
  const pad = size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3.5 py-1.5 text-[13px]'
  return (
    <div className="inline-flex items-center gap-1 rounded-lg bg-surface-secondary border border-border p-0.5">
      {tabs.map((t) => {
        const active = t.key === value
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-md font-semibold transition-colors ${pad} ${
              active ? 'bg-brand-600 text-white shadow-sm' : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

/** Centered modal overlay. Click backdrop or ✕ to close. */
export const Overlay: React.FC<{ onClose: () => void; width?: number; children: React.ReactNode }> = ({ onClose, width = 720, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={onClose}>
      <div
        className="bg-surface border border-border rounded-xl shadow-float w-full flex flex-col"
        style={{ maxWidth: width, maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

export const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover shrink-0">
    ✕
  </button>
)

/* ─── Confirm dialog (used by every archive/delete affordance) ──────────────── */

export interface ConfirmChoice { key: string; label: string; hint?: string }
/**
 * A small confirm modal. Manages its own busy state and awaits an async onConfirm,
 * so callers just do the API call. `choices` renders a radio group (e.g. archive a
 * show "on its own" vs "with its leads") and passes the chosen key to onConfirm.
 */
export const ConfirmDialog: React.FC<{
  title: string
  message?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  choices?: ConfirmChoice[]
  defaultChoice?: string
  onConfirm: (choiceKey?: string) => Promise<void> | void
  onClose: () => void
}> = ({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = true, choices, defaultChoice, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false)
  const [choice, setChoice] = useState(defaultChoice || choices?.[0]?.key)
  const run = async () => {
    if (busy) return
    setBusy(true)
    try { await onConfirm(choice); onClose() } catch (e) { setBusy(false); throw e }
  }
  return (
    <Overlay onClose={busy ? () => {} : onClose} width={440}>
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${destructive ? 'bg-red-50 text-red-600' : 'bg-brand-50 text-brand-600'}`}>
            {destructive ? <IconTrash className="w-4 h-4" /> : '?'}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-txt-primary">{title}</h3>
            {message && <div className="text-sm text-txt-secondary mt-1">{message}</div>}
          </div>
        </div>
        {choices && choices.length > 0 && (
          <div className="mt-4 space-y-2">
            {choices.map((c) => (
              <label key={c.key} className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer ${choice === c.key ? 'border-brand-400 bg-brand-50/40' : 'border-border hover:bg-surface-hover'}`}>
                <input type="radio" name="confirm-choice" className="mt-0.5" checked={choice === c.key} onChange={() => setChoice(c.key)} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-txt-primary">{c.label}</span>
                  {c.hint && <span className="block text-xs text-txt-tertiary mt-0.5">{c.hint}</span>}
                </span>
              </label>
            ))}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-50">
            {cancelLabel}
          </button>
          <button
            onClick={run}
            disabled={busy}
            className={`px-3.5 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-60 ${destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-600 hover:bg-brand-700'}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </Overlay>
  )
}

/* ─── Lightweight CRM toast bus ───────────────────────────────────────────── */

export function crmToast(message: string, kind: 'info' | 'success' | 'error' = 'info') {
  window.dispatchEvent(new CustomEvent('crm-toast', { detail: { message, kind } }))
}

export const CrmToaster: React.FC = () => {
  const [toasts, setToasts] = useState<{ id: number; message: string; kind: string }[]>([])
  useEffect(() => {
    const handler = (e: Event) => {
      const { message, kind } = (e as CustomEvent).detail
      const id = Date.now() + Math.random()
      setToasts((t) => [...t, { id, message, kind }])
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600)
    }
    window.addEventListener('crm-toast', handler)
    return () => window.removeEventListener('crm-toast', handler)
  }, [])
  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-2 items-end">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`px-4 py-2.5 rounded-lg shadow-float text-sm font-medium text-white ${
            t.kind === 'success' ? 'bg-green-600' : t.kind === 'error' ? 'bg-red-600' : 'bg-slate-800'
          }`}
        >
          {t.message}
        </div>
      ))}
    </div>
  )
}

/**
 * Fresh-signup timer + discovery-call badge (Lee, 14 Aug 2026). Shown on every
 * lead whose free trial started within the window: how long since signup, and
 * whether a discovery call has happened. Amber while waiting, green once done.
 * Server data only (trialStartedAt + discoveryCallDone) — nothing re-derived.
 * The mobile app renders the same fields; daily call reminders are mobile-only.
 */
export const TrialDiscoveryBadge: React.FC<{
  trialStartedAt?: string
  discoveryCallDone?: boolean
  stage?: string
  windowDays?: number
}> = ({ trialStartedAt, discoveryCallDone, stage, windowDays = 14 }) => {
  if (!trialStartedAt) return null
  // Live/terminal leads are past the discovery phase — no badge.
  if (stage && ['successfully_signed_up', 'cancelled_subscription', 'closed_lost', 'archived', 'cancellation_requested', 'churned_in_conversation'].includes(stage)) return null
  const ms = Date.now() - new Date(trialStartedAt).getTime()
  if (ms < 0 || ms > windowDays * 86400_000) return null
  const hours = Math.floor(ms / 3600_000)
  const age = hours < 24 ? `${Math.max(1, hours)}h` : `${Math.floor(hours / 24)}d`
  return discoveryCallDone ? (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
      style={{ background: '#DCFCE7', color: '#166534' }}
      title={`Signed up ${age} ago — discovery call done`}
    >
      <IconClock className="w-3 h-3" /> {age} · ✓ discovery call
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
      style={{ background: '#FEF3C7', color: '#92400E' }}
      title={`Signed up ${age} ago — NO discovery call yet. Daily mobile reminders run until Spoke / Booked / Bad number is logged.`}
    >
      <IconClock className="w-3 h-3" /> {age} · no discovery call
    </span>
  )
}
